import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '../../components/Seo';
import { useAuth } from '../../context/AuthContext';
import { listBlogs, getBlogCategories } from '../../services/blogs';

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

function BlogCard({ blog }) {
  return (
    <Link to={`/blog/${blog.slug}`} className="blog-card">
      <div className="blog-img">
        {blog.cover_image
          ? <img src={blog.cover_image} alt={blog.cover_image_alt || blog.title} loading="lazy" />
          : <span>{(blog.category || 'InvFly')[0]}</span>
        }
      </div>
      <div className="blog-body">
        {blog.category && <div className="blog-cat">{blog.category}</div>}
        <div className="blog-title">{blog.title}</div>
        {blog.excerpt && <p style={{ fontSize: '12px', color: 'var(--text-2)', lineHeight: '1.6', marginBottom: '8px' }}>{blog.excerpt}</p>}
        <div className="blog-meta">
          <span>{formatDate(blog.published_at)}</span>
          {blog.read_minutes ? <span> · {blog.read_minutes} min read</span> : null}
        </div>
      </div>
    </Link>
  );
}

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');

  const activeCategory = searchParams.get('category') || '';
  const activeSearch = searchParams.get('q') || '';
  const activePage = parseInt(searchParams.get('page') || '1', 10) || 1;

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await listBlogs({ page: activePage, limit: 12, category: activeCategory || undefined, q: activeSearch || undefined }, token);
      if (res.data?.success) {
        setItems(res.data.data || []);
        setPagination(res.data.pagination || { page: 1, total_pages: 1, total: 0 });
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load blogs');
    } finally {
      setLoading(false);
    }
  }, [activePage, activeCategory, activeSearch, token]);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getBlogCategories();
        if (!cancelled && res.data?.success) setCategories(res.data.data || []);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v == null || v === '') next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next, { replace: false });
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    updateParams({ q: searchInput.trim(), page: 1 });
  };

  const seoDesc = useMemo(
    () => activeCategory
      ? `Latest ${activeCategory} insights and guides from InvFly — practical learning for Indian markets.`
      : 'Insights, guides, and updates on trading and personal finance for Indian markets — InvFly Blog.',
    [activeCategory]
  );

  return (
    <>
      <Seo title={activeCategory ? `${activeCategory} | InvFly Blog` : 'InvFly Blog'} description={seoDesc} />

      <div className="ph">
        <div className="container">
          <div className="ph-tag">Resources</div>
          <div className="ph-title">Blog</div>
          <p className="ph-desc">Concept-driven articles on markets, trading psychology, risk management, and financial education.</p>

          {/* Search */}
          <form onSubmit={onSearchSubmit} style={{ display: 'flex', gap: '8px', marginTop: '20px', maxWidth: '480px' }}>
            <input
              type="search"
              className="fi"
              placeholder="Search articles…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              style={{ flex: 1 }}
            />
            {searchInput && (
              <button type="button" className="btn-s" style={{ padding: '10px 14px', fontSize: '13px' }} onClick={() => { setSearchInput(''); updateParams({ q: '', page: 1 }); }}>
                Clear
              </button>
            )}
            <button type="submit" className="btn-p" style={{ padding: '10px 18px' }}>Search</button>
          </form>
        </div>
      </div>

      {/* Category chips */}
      {categories.length > 0 && (
        <div style={{ borderBottom: '1px solid var(--border)', padding: '12px 0', background: 'var(--surface)' }}>
          <div className="container">
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                className={`tab${!activeCategory ? ' active' : ''}`}
                onClick={() => updateParams({ category: '', page: 1 })}
              >
                All
              </button>
              {categories.map(c => (
                <button
                  key={c.name}
                  className={`tab${activeCategory === c.name ? ' active' : ''}`}
                  onClick={() => updateParams({ category: activeCategory === c.name ? '' : c.name, page: 1 })}
                >
                  {c.name} {c.count ? <span style={{ opacity: 0.7 }}>({c.count})</span> : null}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <section className="sec">
        <div className="container">
          {error && (
            <div style={{ background: 'rgba(255,77,106,.08)', border: '1px solid rgba(255,77,106,.2)', borderRadius: 'var(--rs)', padding: '12px 16px', color: 'var(--red)', fontSize: '13px', marginBottom: '24px' }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', border: '3px solid var(--border)', borderTopColor: 'var(--accent)', animation: 'spin 0.8s linear infinite' }}></div>
            </div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ fontFamily: 'var(--fd)', fontSize: '18px', fontWeight: '600', color: 'var(--text)' }}>No posts found</div>
            </div>
          ) : (
            <>
              <div className="blog-grid">
                {items.map(b => <BlogCard key={b.id || b.slug} blog={b} />)}
              </div>

              {pagination.total_pages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '40px' }}>
                  <button className="btn-s" style={{ padding: '8px 18px', fontSize: '13px' }} disabled={activePage <= 1} onClick={() => updateParams({ page: activePage - 1 })}>
                    Previous
                  </button>
                  <span style={{ fontSize: '13px', color: 'var(--text-2)', fontFamily: 'var(--fm)' }}>
                    Page {pagination.page} of {pagination.total_pages}
                  </span>
                  <button className="btn-s" style={{ padding: '8px 18px', fontSize: '13px' }} disabled={activePage >= pagination.total_pages} onClick={() => updateParams({ page: activePage + 1 })}>
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

export default Blog;
